import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  InputAdornment,
  Skeleton,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AccountTree as TreeIcon,
  OpenInNew as OpenInNewIcon,
  QuestionAnswer as QuestionIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { paaTreeApiService, PAATree } from '../../services/paaTreeApiService';
import { getCountryFlag } from '../../utils/countryFlags';
import { useNavigate } from 'react-router-dom';
import WebsiteSelector from '../../components/common/WebsiteSelector';
import PAAContentAnalysis from '../../components/paa/PAAContentAnalysis';
import PAABatchAnalysis from '../../components/paa/PAABatchAnalysis';

export const PAATreesPage: React.FC = () => {
  const navigate = useNavigate();
  const selectedWebsite = useSelector((state: RootState) => state.website.selectedWebsite);
  
  const [trees, setTrees] = useState<PAATree[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredTrees, setFilteredTrees] = useState<PAATree[]>([]);

  useEffect(() => {
    if (selectedWebsite) {
      loadTrees();
    }
  }, [selectedWebsite]);

  useEffect(() => {
    // Filter trees based on search query
    if (searchQuery) {
      const filtered = trees.filter(tree =>
        tree.root_keyword.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredTrees(filtered);
    } else {
      setFilteredTrees(trees);
    }
  }, [searchQuery, trees]);

  const loadTrees = async () => {
    if (!selectedWebsite) {
      setTrees([]);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const { trees: data, error } = await paaTreeApiService.getTrees(
        selectedWebsite.website_id
      );
      
      if (error) {
        setError(error);
      } else {
        setTrees(data);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load PAA trees');
    } finally {
      setLoading(false);
    }
  };

  const handleViewTree = (treeId: number) => {
    navigate(`/paa-trees/${treeId}`);
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TreeIcon sx={{ fontSize: 32, color: '#7b61ff' }} />
              People Also Ask Trees
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Visualize and explore question trees generated from your keywords
            </Typography>
          </Box>
          <WebsiteSelector showAddButton={false} />
        </Box>
      </Box>

      {/* Search and Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            placeholder="Search by keyword..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
          <IconButton onClick={loadTrees} sx={{ border: '1px solid #e0e0e0' }}>
            <RefreshIcon />
          </IconButton>
          {selectedWebsite && (
            <PAABatchAnalysis websiteId={selectedWebsite.website_id} />
          )}
        </Box>
      </Paper>

      {/* Trees Grid */}
      {!selectedWebsite ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <TreeIcon sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Select a Website
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please select a website from the dropdown above to view its PAA trees
          </Typography>
        </Paper>
      ) : loading ? (
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} md={6} lg={4} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width="60%" height={32} />
                  <Skeleton variant="text" width="40%" sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Skeleton variant="rounded" width={80} height={24} />
                    <Skeleton variant="rounded" width={60} height={24} />
                  </Box>
                  <Skeleton variant="rectangular" height={40} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : filteredTrees.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <TreeIcon sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No PAA Trees Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchQuery
              ? 'Try adjusting your search criteria'
              : 'PAA trees will appear here once they are generated'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredTrees.map((tree) => (
            <Grid item xs={12} md={6} lg={4} key={tree.tree_id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <CardContent sx={{ flex: 1 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    {tree.root_keyword}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <Chip
                      label={getCountryFlag(tree.country) + ' ' + tree.country}
                      size="small"
                      variant="outlined"
                    />
                    <Tooltip 
                      title={
                        tree.processing_status === 'failed' 
                          ? 'Tree generation failed - this can happen if Google doesn\'t show PAA questions for this keyword'
                          : tree.processing_status === 'processing'
                          ? 'Tree is currently being generated'
                          : 'Tree generation completed successfully'
                      }
                    >
                      <Chip
                        label={tree.status || tree.processing_status || 'Unknown'}
                        size="small"
                        color={getStatusColor(tree.status || tree.processing_status || 'unknown')}
                      />
                    </Tooltip>
                    {tree.total_questions && tree.total_questions > 0 && (
                      <Chip
                        icon={<QuestionIcon sx={{ fontSize: 16 }} />}
                        label={`${tree.total_questions} questions`}
                        size="small"
                        sx={{ bgcolor: 'rgba(123, 97, 255, 0.1)' }}
                      />
                    )}
                  </Box>

                  {(tree.created_at || tree.created_date) && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Created: {new Date(tree.created_at || tree.created_date!).toLocaleDateString()}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<OpenInNewIcon />}
                      onClick={() => handleViewTree(tree.tree_id)}
                      disabled={tree.processing_status === 'failed' || tree.processing_status === 'processing'}
                      sx={{
                        bgcolor: tree.processing_status === 'failed' ? undefined : '#7b61ff',
                        '&:hover': { 
                          bgcolor: tree.processing_status === 'failed' ? undefined : '#6250e6' 
                        },
                      }}
                    >
                      {tree.processing_status === 'failed' ? 'No Data' : tree.processing_status === 'processing' ? 'Processing...' : 'View Tree'}
                    </Button>
                    {tree.processing_status === 'completed' && tree.url_id && (
                      <PAAContentAnalysis 
                        urlId={tree.url_id}
                        url={tree.root_keyword}
                        questionCount={tree.total_questions || 0}
                        onAnalysisComplete={loadTrees}
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};